import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "../shared/Reveal";
import { fmtEur } from "../../lib/api";
import { FLOW, METHOD_STEPS, PHASES } from "./operationsData";

export const FlowBand = () => (
  <div data-testid="op-flow" className="flex flex-wrap items-center justify-center gap-x-4 gap-y-6 md:gap-x-8 border-y border-ink/10 py-10 md:py-12">
    {FLOW.map((w, i) => (
      <Reveal key={w} delay={i * 0.12} className="flex items-center gap-4 md:gap-8">
        <span className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-ink">{w}</span>
        {i < FLOW.length - 1 && <ArrowRight size={22} strokeWidth={1} className="text-gold" />}
      </Reveal>
    ))}
  </div>
);

const ScenarioCard = ({ label, s, featured, delay }) => (
  <Reveal delay={delay} data-testid={`op-scenario-${label.toLowerCase()}`} className={`relative flex flex-col p-10 md:p-12 ${featured ? "bg-ink text-cream lg:-my-6 lg:py-16 shadow-2xl shadow-ink/20" : "bg-white border border-ink/10 text-ink"}`}>
    {featured && <div className="absolute top-0 left-10 md:left-12 right-10 md:right-12 h-px bg-gold" />}
    <div className={`text-[11px] uppercase tracking-[0.25em] ${featured ? "text-gold" : "text-stone"}`}>Scenario {label}</div>
    <div className={`font-serif font-light tracking-tight leading-none mt-8 ${featured ? "text-6xl md:text-7xl lg:text-[5.25rem]" : "text-5xl md:text-6xl lg:text-7xl"}`} data-testid={`op-scenario-${label.toLowerCase()}-value`}>{s?.value != null ? fmtEur(s.value) : "—"}</div>
    <div className={`w-12 h-px mt-8 mb-6 ${featured ? "bg-gold" : "bg-sage"}`} />
    <p className={`text-sm md:text-base font-light leading-relaxed ${featured ? "text-cream/75" : "text-ink/70"}`}>{s?.text}</p>
    {featured && <div className="mt-8 text-[10px] uppercase tracking-[0.22em] text-cream/50">Ipotesi centrale · non garantita</div>}
  </Reveal>
);

export const Scenarios = ({ o }) => (
  <section className="section bg-cream overflow-hidden" id="scenari">
    <div className="container-hh">
      <Reveal className="max-w-3xl mb-16 md:mb-24"><div className="eyebrow mb-5">Scenari di valorizzazione</div><h2 className="h-section">Tre scenari di valorizzazione.</h2><p className="lead mt-6">L'analisi dell'operazione viene sviluppata utilizzando tre differenti ipotesi di uscita, in modo da valutare il progetto senza basarlo esclusivamente sullo scenario più favorevole.</p></Reveal>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-0 lg:items-center">
        <ScenarioCard label="Prudente" s={o.scenario_prudente} delay={0} />
        <ScenarioCard label="Atteso" s={o.scenario_atteso} featured delay={0.12} />
        <ScenarioCard label="Migliorativo" s={o.scenario_migliorativo} delay={0.24} />
      </div>
      <Reveal className="mt-14 md:mt-20 border-l-2 border-gold pl-6 md:pl-8 max-w-3xl"><p data-testid="op-scenarios-note" className="text-sm md:text-base font-light leading-relaxed text-ink/75">{o.scenarios_note}</p></Reveal>
    </div>
  </section>
);

export const MethodSteps = () => (
  <section className="section bg-white">
    <div className="container-hh">
      <Reveal className="mb-16 md:mb-20"><div className="eyebrow mb-5">Il metodo Home Haven</div><h2 className="h-section">Sette passaggi, un unico controllo.</h2></Reveal>
      <div className="relative">
        <div className="hidden lg:block absolute top-[38px] left-0 right-0 h-px bg-ink/10"><Reveal className="h-full bg-gold origin-left" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 2.2, ease: "easeInOut" }} /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-12 lg:gap-4">
          {METHOD_STEPS.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.1} data-testid={`op-method-${m.n}`}>
              <div className="relative z-10 w-[76px] h-[76px] rounded-full border border-sage/50 bg-white flex items-center justify-center"><m.icon size={22} strokeWidth={1} className="text-sage" /></div>
              <div className="mt-6 font-serif text-4xl font-light text-gold leading-none">{m.n}</div>
              <h3 className="mt-3 font-sans text-xs uppercase tracking-[0.25em] font-medium text-ink">{m.title}</h3>
              <p className="mt-3 text-sm font-light leading-relaxed text-ink/70">{m.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const PhaseTimeline = ({ phase }) => {
  const idx = Math.max(0, PHASES.findIndex(([k]) => k === phase));
  return (
    <section className="section bg-ink text-cream">
      <div className="container-hh">
        <Reveal className="mb-16 md:mb-20"><div className="eyebrow mb-5">Avanzamento operazione</div><h2 className="h-section">Dove siamo oggi.</h2></Reveal>
        <ol data-testid="op-phase-timeline" className="relative grid grid-cols-1 md:grid-cols-6 gap-10 md:gap-4">
          <div className="hidden md:block absolute top-[15px] left-0 right-0 h-px bg-white/10"><div className="h-full bg-gold transition-all duration-1000" style={{ width: `${(idx / (PHASES.length - 1)) * 100}%` }} /></div>
          {PHASES.map(([k, label], i) => {
            const done = i < idx, current = i === idx;
            return (
              <Reveal as="li" key={k} delay={i * 0.08} data-testid={`op-phase-${k}`} data-current={current} className="relative flex md:block items-start gap-5">
                <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center border shrink-0 ${current ? "bg-gold border-gold text-ink ring-8 ring-gold/15" : done ? "bg-cream border-cream text-ink" : "border-white/25 text-cream/40"}`}>{done ? <Check size={14} /> : <span className="text-[11px]">{i + 1}</span>}</div>
                <div className="md:mt-6">
                  <div className={`text-[11px] uppercase tracking-[0.22em] ${current ? "text-gold" : done ? "text-cream/80" : "text-cream/40"}`}>{label}</div>
                  {current && <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-cream/60">Fase attuale</div>}
                </div>
              </Reveal>
            );
          })}
        </ol>
      </div>
    </section>
  );
};
